import React, { useState, useContext } from "react";
import "./Auth.css";
import carImage from "../../Assets/carLogin.jpg";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { MyContext } from "../Context/MyContext";
import { FormPageSkeleton } from "../Components/Skeleton/Skeleton";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";
import { authApi, setAccessToken } from "../../services/api";

const AuthForm = ({ pageConfig, roles, formData, setFormData, onSubmit, onSwitchMode, error, loading }) => {
  const renderRoleField = () => {
    if (!pageConfig.role) return null;

    return (
      <div className="auth-field full">
        <label className="auth-label">{pageConfig.roleLabel}</label>
        <select
          value={formData.role || ""}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
        >
          <option value="">{pageConfig.roleLabel}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.value}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderFieldsInOrder = () => {
    const elements = [];
    let halfBuffer = [];

    const flushHalfRow = () => {
      if (halfBuffer.length === 0) return;
      elements.push(
        <div className="row" key={`row-${elements.length}`}>
          {halfBuffer.map((field) => (
            <div className="auth-field col" key={field.name}>
              <label className="auth-label">{field.label}</label>
              <input
                type={field.type}
                placeholder={field.placeholder}
                value={formData[field.name] || ""}
                onChange={(e) =>
                  setFormData({ ...formData, [field.name]: e.target.value })
                }
              />
            </div>
          ))}
        </div>
      );
      halfBuffer = [];
    };

    pageConfig.fields.forEach((field) => {
      if (field.layout === "half") {
        halfBuffer.push(field);
        if (halfBuffer.length === 2) flushHalfRow();
      } else {
        flushHalfRow();
        elements.push(
          <div className="auth-field full" key={field.name}>
            <label className="auth-label">{field.label}</label>
            <input
              type={field.type}
              placeholder={field.placeholder}
              value={formData[field.name] || ""}
              onChange={(e) =>
                setFormData({ ...formData, [field.name]: e.target.value })
              }
            />
          </div>
        );
      }
    });

    flushHalfRow();
    return elements;
  };

  return (
    <div className="auth-form auth-card">
      <div className="auth-form-header">
        <h2>{pageConfig.title}</h2>
        <p className="auth-subtitle">{pageConfig.subtitle}</p>
      </div>

      {error && <p className="auth-error">{error}</p>}

      {renderRoleField()}
      {renderFieldsInOrder()}

      {pageConfig.forgotPassword?.show && (
        <p
          className="forgot-link"
          onClick={() => onSwitchMode(pageConfig.forgotPassword.action)}
        >
          {pageConfig.forgotPassword.text}
        </p>
      )}

      <button type="button" className="auth-submit" onClick={onSubmit} disabled={loading}>
        {loading ? "Please wait..." : pageConfig.submitLabel}
      </button>

      {pageConfig.footer && (
        <p className="auth-footer" onClick={() => onSwitchMode(pageConfig.footer.action)}>
          {pageConfig.footer.text} <span>{pageConfig.footer.linkText}</span>
        </p>
      )}
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { auth, refreshCurrentUser } = useContext(MyContext);

  const getInitialMode = () => {
    const modeParam = searchParams.get("mode");
    if (["login", "signup", "forgot"].includes(modeParam)) return modeParam;
    if (location.pathname.includes("forgot")) return "forgot";
    return "login";
  };

  const [mode, setMode] = useState(getInitialMode);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ identifier: "", password: "", role: "" });
  const [signupData, setSignupData] = useState({
    role: "",
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [forgotData, setForgotData] = useState({ email: "", role: "" });

  const handleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await authApi.login({
        identifier: loginData.identifier,
        password: loginData.password,
      });
      setAccessToken(res.accessToken);
      await refreshCurrentUser();
      navigate(res.user?.role === "admin" ? "/admin" : "/cars");
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setError("");
    if (signupData.password !== signupData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.signup({
        name: signupData.name,
        email: signupData.email,
        phone: signupData.phone,
        password: signupData.password,
        role: signupData.role || "user",
      });
      setAccessToken(res.accessToken);
      await refreshCurrentUser();
      navigate("/cars");
    } catch (err) {
      setError(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = () => {
    alert("Password reset link sent to your email!");
    setMode("login");
  };

  const pageConfigMap = {
    login: auth.pages.login,
    signup: auth.pages.signup,
    forgotPassword: auth.pages.forgotPassword,
  };

  const formDataMap = {
    login: { data: loginData, setData: setLoginData, handler: handleLogin },
    signup: { data: signupData, setData: setSignupData, handler: handleSignup },
    forgotPassword: { data: forgotData, setData: setForgotData, handler: handleForgot },
  };

  const currentKey = mode === "forgot" ? "forgotPassword" : mode;
  const pageConfig = pageConfigMap[currentKey];
  const { data, setData, handler } = formDataMap[currentKey];

  return (
    <PageLoadGate
      skeleton={
        <div className="auth-container">
          <div className="form-side" style={{ width: "100%" }}>
            <FormPageSkeleton />
          </div>
        </div>
      }
      delay={380}
    >
      <div className={`auth-container ${mode === "signup" ? "signup-mode" : ""}`}>
        <div className="image-side">
          <div className="image-bg" style={{ backgroundImage: `url(${carImage})` }}>
            <div className="image-overlay-content">
              <h1>CarZone</h1>
              <p>Find your perfect ride today</p>
            </div>
          </div>
        </div>

        <div className="form-side">
          <AuthForm
            pageConfig={pageConfig}
            roles={auth.roles}
            formData={data}
            setFormData={setData}
            onSubmit={handler}
            onSwitchMode={(action) => {
              setError("");
              if (action === "forgot") setMode("forgot");
              else if (action === "signup") setMode("signup");
              else setMode("login");
            }}
            error={error}
            loading={loading}
          />
        </div>
      </div>
    </PageLoadGate>
  );
};

export default Login;
